import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { AgentMessage as AgentMessageType, AgentAction } from '@/hooks/useAuraAgent';
import { Button } from '@/components/ui/button';
import { FeedbackButtons } from './FeedbackButtons';
import { TypingIndicator } from '@/components/ui/skeletons';
import auraLogo from '@/assets/aura-logo.png';

interface AgentMessageProps {
  message: AgentMessageType;
  onAction?: (action: AgentAction) => void;
  interactionId?: string | null;
}

export const AgentMessage: React.FC<AgentMessageProps> = ({ message, onAction, interactionId }) => {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming && !message.isLoading;
  const showCursor = isStreaming && message.content;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'flex gap-3 max-w-[85%]',
        isUser ? 'ml-auto flex-row-reverse' : 'mr-auto'
      )}
    >
      {/* Avatar */}
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center overflow-hidden">
          <img src={auraLogo} alt="AURA" className="w-6 h-6 object-contain" />
        </div>
      )}

      {/* Message Bubble */}
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            'rounded-2xl px-4 py-3 transition-all',
            isUser 
              ? 'bg-primary text-primary-foreground rounded-br-sm' 
              : 'bg-card border border-border rounded-bl-sm'
          )}
        >
          {message.isLoading ? (
            <TypingIndicator />
          ) : (
            <>
              <p className={cn(
                'text-sm leading-relaxed',
                isUser ? 'text-primary-foreground' : 'text-card-foreground'
              )}>
                {message.content}
                {showCursor && (
                  <span className="inline-block w-0.5 h-4 bg-aura-gold ml-0.5 animate-pulse align-middle" />
                )}
              </p>

              {/* Action Buttons - Only show when not streaming */}
              {!isStreaming && message.actions && message.actions.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex flex-wrap gap-2 mt-3"
                >
                  {message.actions.map((action, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => onAction?.(action)}
                      className="text-xs h-8 rounded-full border-aura-gold/50 hover:bg-aura-gold/10 hover:border-aura-gold transition-colors"
                    >
                      {action.label}
                    </Button>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </div>

        {/* Feedback Buttons - Only for completed agent messages */}
        {!isUser && !message.isLoading && !message.isStreaming && interactionId && (
          <FeedbackButtons interactionId={interactionId} className="ml-2 mt-1" />
        )}
      </div>
    </motion.div>
  );
};
